import React, { useState } from "react";
import DOMPurify from "dompurify";
import {
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaHistory,
  FaImage,
  FaSyncAlt,
} from "react-icons/fa";

import {
  getMediaUrl,
  stripHtml,
} from "../utils/leClubUtils";

export default function HistoryPanel({
  loading,
  settings,
  onEdit,
  onRefresh,
}) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = [
    settings?.historique_photo_url,
    settings?.historique_photo2_url,
    settings?.historique_photo3_url,
  ].filter(Boolean);

  const hasContent = Boolean(stripHtml(settings?.historique_content));

  const nextImage = () => {
    if (!images.length) return;
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    if (!images.length) return;
    setCurrentImageIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  if (loading) {
    return (
      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="h-72 animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800" />
        <div className="mt-5 h-8 w-64 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
        <div className="mt-4 h-24 animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800" />
      </section>
    );
  }

  return (
    <section className="space-y-5">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              Historique du club
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Prévisualisez la présentation affichée côté visiteur.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
            >
              <FaEdit />
              Modifier
            </button>

            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaSyncAlt />
              Actualiser
            </button>
          </div>
        </div>
      </div>

      <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
        <div className="relative min-h-[320px] bg-slate-100 dark:bg-slate-800">
          {images.length ? (
            <img
              src={getMediaUrl(images[currentImageIndex])}
              alt={`Historique ${currentImageIndex + 1}`}
              className="h-[320px] w-full object-cover"
            />
          ) : (
            <div className="flex h-[320px] flex-col items-center justify-center text-slate-400">
              <FaImage className="text-5xl" />
              <p className="mt-3 text-sm font-bold">Aucune image renseignée</p>
            </div>
          )}

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={prevImage}
                className="absolute left-4 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-2xl bg-white/90 text-slate-700 shadow-lg backdrop-blur transition hover:bg-white dark:bg-slate-950/80 dark:text-white"
              >
                <FaChevronLeft />
              </button>

              <button
                type="button"
                onClick={nextImage}
                className="absolute right-4 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-2xl bg-white/90 text-slate-700 shadow-lg backdrop-blur transition hover:bg-white dark:bg-slate-950/80 dark:text-white"
              >
                <FaChevronRight />
              </button>
            </>
          )}

          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 to-transparent p-5">
            <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-orange-200">
              <FaHistory />
              Historique
            </p>

            <h3 className="mt-1 text-3xl font-bold text-white">
              {settings?.historique_title || "Titre non renseigné"}
            </h3>
          </div>
        </div>

        <div className="p-6">
          {hasContent ? (
            <div
              className="prose max-w-none prose-slate dark:prose-invert"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(settings.historique_content),
              }}
            />
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950">
              <FaHistory className="mx-auto text-3xl text-slate-400" />
              <p className="mt-3 text-sm font-medium text-slate-500 dark:text-slate-400">
                Aucun contenu historique renseigné pour le moment.
              </p>
            </div>
          )}
        </div>
      </article>
    </section>
  );
}